import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faPlus, 
  faRefresh, 
  faCalendarAlt, 
  faClock, 
  faRepeat, 
  faCheckCircle, 
  faExclamationTriangle,
  faExpand,
  faCog,
  faSync,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import WarehouseTaskForm from './WarehouseTaskForm';
import MessageModal from './MessageModal';

import { API_BASE_URL, buildApiUrl } from '../config/api';

interface WarehouseTask {
  id: number;
  event_name: string;
  description: string;
  task_type: string;
  notes: string;
  start_date: string;
  end_date: string;
  status: string;
  is_recurring: boolean;
  recurrence_pattern: string;
  custom_recurrence_days: number | null;
  recurrence_end_date: string | null;
  next_occurrence: string | null;
  color: any;
  created_by: any;
  created_at: string;
  parent_warehouse_task?: number;
}

const TASK_TYPE_LABELS: { [key: string]: string } = {
  maintenance: 'תחזוקה',
  inspection: 'בדיקה תקופתית',
  testing: 'בדיקה/טסט',
  cleaning: 'ניקוי',
  calibration: 'כיול',
  inventory_check: 'בדיקת מלאי',
  safety_check: 'בדיקת בטיחות',
  other: 'אחר'
};

const STATUS_LABELS: { [key: string]: string } = {
  planned: 'מתוכנן',
  in_progress: 'בביצוע',
  completed: 'הושלם',
  cancelled: 'בוטל',
  no_equipment_list: 'ללא רשימת ציוד'
};

const RECURRENCE_LABELS: { [key: string]: string } = {
  none: 'לא מחזורי',
  daily: 'יומי',
  weekly: 'שבועי',
  monthly: 'חודשי',
  quarterly: 'רבעוני',
  biannual: 'חצי שנתי',
  annual: 'שנתי',
  custom: 'מותאם אישית'
};

const WarehouseTaskList: React.FC = () => {
  const [tasks, setTasks] = useState<WarehouseTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<WarehouseTask | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
  
  // הודעות מעוצבות
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalTitle, setMessageModalTitle] = useState('');
  const [messageModalText, setMessageModalText] = useState('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  const [messageModalAction, setMessageModalAction] = useState<(() => void) | undefined>(undefined);
  const [messageModalConfirmText, setMessageModalConfirmText] = useState('');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('warehouse/tasks/'));
      setTasks(response.data);
    } catch (error: any) {
      console.error('שגיאה בטעינת משימות:', error);
    } finally {
      setLoading(false);
    }
  };

  const showCustomMessageModal = (
    title: string,
    text: string, 
    variant: 'success' | 'danger' | 'info' | 'warning' = 'info',
    action?: () => void,
    confirmText?: string
  ) => {
    console.log('showCustomMessageModal called:', { title, text, variant, hasAction: !!action, confirmText });
    setMessageModalTitle(title);
    setMessageModalText(text);
    setMessageModalVariant(variant);
    setMessageModalAction(action ? () => action : undefined);
    setMessageModalConfirmText(confirmText || '');
    setShowMessageModal(true);
  };

  const hideMessageModal = () => {
    console.log('hideMessageModal called');
    setShowMessageModal(false);
    setMessageModalAction(undefined);
  };

  const toggleExpanded = (taskId: number) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  // קיבוץ משימות לפי משימה מקורית
  const groupedTasks = tasks.reduce((groups, task) => {
    const parentId = task.parent_warehouse_task || task.id;
    if (!groups[parentId]) {
      groups[parentId] = [];
    }
    groups[parentId].push(task);
    return groups;
  }, {} as { [key: number]: WarehouseTask[] });

  // מיון המשימות בכל קבוצה לפי תאריך
  Object.keys(groupedTasks).forEach(key => {
    groupedTasks[parseInt(key)].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  });

  const handleCreateTask = async (taskData: any) => {
    try {
      const response = await axios.post(buildApiUrl('warehouse/tasks/'), taskData);
      setShowForm(false);
      fetchTasks();
      showCustomMessageModal('הצלחה', `המשימה "${response.data?.description || response.data?.event_name || ''}" נוצרה בהצלחה`, 'success');
    } catch (error: any) {
      console.error('שגיאה ביצירת משימה:', error);
      showCustomMessageModal('שגיאה', 'שגיאה ביצירת המשימה', 'danger');
      throw error;
    }
  };

  const handleUpdateTask = async (taskData: any) => {
    if (!editingTask) return;
    
    try {
      await axios.put(buildApiUrl(`warehouse/tasks/${editingTask.id}/`), taskData);
      setEditingTask(null);
      fetchTasks();
      showCustomMessageModal('הצלחה', `המשימה "${editingTask.event_name}" עודכנה בהצלחה`, 'success');
    } catch (error: any) {
      console.error('שגיאה בעדכון משימה:', error);
      showCustomMessageModal('שגיאה', 'שגיאה בעדכון המשימה', 'danger');
      throw error;
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    showCustomMessageModal(
      'סכנה',
      'האם אתה בטוח שברצונך למחוק משימה זו?',
      'danger',
      async () => {
        try {
          const task = tasks.find(t => t.id === taskId);
          await axios.delete(buildApiUrl(`warehouse/tasks/${taskId}/`));
          hideMessageModal();
          fetchTasks();
          showCustomMessageModal('הצלחה', `המשימה ${task ? `"${task.event_name}" ` : ''}נמחקה בהצלחה`, 'success');
        } catch (error: any) {
          hideMessageModal();
          console.error('שגיאה במחיקת משימה:', error);
          showCustomMessageModal('שגיאה', 'שגיאה במחיקת המשימה', 'danger');
        }
      },
      'מחק'
    );
  };

  const handleCreateFutureRecurrences = async (taskId: number) => {
    try {
      const response = await axios.post(buildApiUrl(`warehouse/tasks/${taskId}/create_future_recurrences/`));
      fetchTasks();
      
      // בדיקה אם נוצרו אירועים חדשים
      if (response.data && response.data.created_events) {
        showCustomMessageModal('הצלחה', `נוצרו ${response.data.created_events.length} אירועים מחזוריים חדשים עבור המשימה בהצלחה`, 'success');
      } else {
        showCustomMessageModal('הצלחה', 'האירועים המחזוריים עבור המשימה נוצרו בהצלחה', 'success');
      }
    } catch (error: any) {
      console.error('שגיאה ביצירת אירועים עתידיים:', error);
      showCustomMessageModal('שגיאה', 'שגיאה ביצירת האירועים המחזוריים', 'danger');
    }
  };

  const handleEditAllRecurringTasks = async (parentTaskId: number) => {
    try {
      // מציאת המשימה המקורית
      const parentTask = tasks.find(task => task.id === parentTaskId);
      if (!parentTask) return;
      
      // פתיחת הטופס לעריכה עם נתוני המשימה המקורית
      setEditingTask(parentTask);
    } catch (error: any) {
      console.error('שגיאה בפתיחת עריכת כל המשימות המחזוריות:', error);
    }
  };

  const handleDeleteAllRecurringTasks = async (parentTaskId: number) => {
    showCustomMessageModal(
      'סכנה',
      'האם אתה בטוח שברצונך למחוק את כל המשימות המחזוריות? פעולה זו תמחק את המשימה המקורית ואת כל המשימות המשנה.',
      'danger',
      async () => {
        try {
          // מחיקת כל המשימות המשנה קודם
          const childTasks = tasks.filter(task => task.parent_warehouse_task === parentTaskId);
          for (const childTask of childTasks) {
            await axios.delete(buildApiUrl(`warehouse/tasks/${childTask.id}/`));
          }
          
          // מחיקת המשימה המקורית
          await axios.delete(buildApiUrl(`warehouse/tasks/${parentTaskId}/`));
          
          hideMessageModal();
          fetchTasks();
          showCustomMessageModal('הצלחה', 'כל המשימות המחזוריות נמחקו בהצלחה', 'success');
        } catch (error: any) {
          hideMessageModal();
          console.error('שגיאה במחיקת כל המשימות המחזוריות:', error);
          showCustomMessageModal('שגיאה', 'שגיאה במחיקת המשימות המחזוריות', 'danger');
        }
      },
      'מחק הכל'
    );
  };

  const handleCheckEquipmentStatus = async (taskId: number) => {
    try {
      await axios.post(buildApiUrl(`warehouse/tasks/${taskId}/check_equipment_status/`));
      fetchTasks();
      showCustomMessageModal('הצלחה', 'סטטוס הציוד למשימה נבדק והעודכן בהצלחה', 'success');
    } catch (error: any) {
      console.error('שגיאה בבדיקת סטטוס הציוד:', error);
      showCustomMessageModal('שגיאה', 'שגיאה בבדיקת סטטוס הציוד', 'danger');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">טוען...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>משימות מחסן</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          משימה חדשה
        </button>
      </div>

      {/* רשימת המשימות */}
      <div className="row g-3">
        {Object.keys(groupedTasks).length === 0 ? (
          <div className="col-12">
            <div className="text-center p-5">
              <div className="text-muted">
                <FontAwesomeIcon icon={faCog} size="3x" className="mb-3" />
                <h5>אין משימות מחסן</h5>
                <p>לחץ על "משימה חדשה" כדי ליצור משימת מחסן ראשונה</p>
              </div>
            </div>
          </div>
        ) : (
          Object.entries(groupedTasks).map(([parentId, tasks]) => (
            <div key={parentId} className="col-12">
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="card-title mb-0">
                      {tasks[0].event_name}
                      {tasks.length > 1 && (
                        <span className="badge bg-info ms-2">
                          {tasks.length} משימות משנה
                        </span>
                      )}
                    </h5>
                    
                    {/* פרטי המשימה בכותרת */}
                    <div className="mt-2">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge bg-primary">
                          {TASK_TYPE_LABELS[tasks[0].task_type] || tasks[0].task_type}
                        </span>
                        {tasks[0].is_recurring && (
                          <span className="badge bg-info">
                            <FontAwesomeIcon icon={faRepeat} className="me-1" />
                            {RECURRENCE_LABELS[tasks[0].recurrence_pattern] || tasks[0].recurrence_pattern}
                          </span>
                        )}
                      </div>
                      
                      {tasks[0].description && (
                        <p className="text-muted mb-1 small">
                          <strong>תיאור:</strong> {tasks[0].description}
                        </p>
                      )}
                      
                      {tasks[0].notes && (
                        <p className="text-muted mb-0 small">
                          <strong>הערות:</strong> {tasks[0].notes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2 align-items-start">
                    {/* כפתורי פעולה לכל המשימות המחזוריות בבת אחת */}
                    {tasks[0].is_recurring && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleCreateFutureRecurrences(tasks[0].id)}
                          title="צור את כל האירועים המחזוריים העתידיים"
                        >
                          <FontAwesomeIcon icon={faRepeat} className="me-1" />
                          צור אירועים עתידיים
                        </button>
                        
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditAllRecurringTasks(tasks[0].id)}
                          title="ערוך את כל המשימות המחזוריות בבת אחת"
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          ערוך הכל
                        </button>
                        
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteAllRecurringTasks(tasks[0].id)}
                          title="מחק את כל המשימות המחזוריות בבת אחת"
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          מחק הכל
                        </button>
                      </>
                    )}
                    
                    {/* כפתורי פעולה למשימות בודדות */}
                    {tasks.length === 1 && !tasks[0].is_recurring && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setEditingTask(tasks[0])}
                        title="ערוך משימה"
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        ערוך
                      </button>
                    )}
                    
                    {/* כפתור ערוך למשימות מחזוריות בודדות */}
                    {tasks.length === 1 && tasks[0].is_recurring && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setEditingTask(tasks[0])}
                        title="ערוך משימה מחזורית"
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        ערוך
                      </button>
                    )}
                    
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleExpanded(parseInt(parentId))}
                    >
                      <FontAwesomeIcon icon={expandedTasks.has(parseInt(parentId)) ? faChevronUp : faChevronDown} />
                    </button>
                  </div>
                </div>
                {expandedTasks.has(parseInt(parentId)) && (
                  <div className="card-body">
                    {tasks.map((task) => (
                      <div key={task.id} className="card mb-2">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-8">
                              <h6 className="card-title">{task.event_name}</h6>
                              
                              {/* תצוגת תאריכים בלבד */}
                              <div className="row text-muted small">
                                <div className="col-md-6">
                                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                  התחלה: {formatDate(task.start_date)}
                                </div>
                                <div className="col-md-6">
                                  <FontAwesomeIcon icon={faClock} className="me-1" />
                                  סיום: {formatDate(task.end_date)}
                                </div>
                              </div>
                              
                              {/* סטטוס המשימה */}
                              <div className="mt-2">
                                <span className={`badge ${task.status === 'completed' ? 'bg-success' : task.status === 'cancelled' ? 'bg-danger' : 'bg-warning'}`}>
                                  {STATUS_LABELS[task.status] || task.status}
                                </span>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="d-flex flex-column gap-2">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => setEditingTask(task)}
                                >
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  ערוך
                                </button>
                                
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteTask(task.id)}
                                >
                                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                                  מחק
                                </button>

                                {task.is_recurring && (
                                  <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => handleCreateFutureRecurrences(task.id)}
                                    title="צור את כל האירועים המחזוריים העתידיים"
                                  >
                                    <FontAwesomeIcon icon={faRepeat} className="me-1" />
                                    צור אירועים עתידיים
                                  </button>
                                )}

                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => handleCheckEquipmentStatus(task.id)}
                                >
                                  <FontAwesomeIcon icon={faCog} className="me-1" />
                                  בדוק סטטוס ציוד
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* טופס יצירת/עריכת משימה */}
      <div className={`modal fade ${showForm || editingTask ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingTask ? 'ערוך משימה' : 'משימה חדשה'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  setEditingTask(null);
                }}
              />
            </div>
            <div className="modal-body">
              <WarehouseTaskForm
                onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
                initialData={editingTask}
                isEdit={!!editingTask}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* הודעה מעוצבת - MessageModal */}
      <MessageModal
        show={showMessageModal}
        onHide={hideMessageModal}
        title={messageModalTitle}
        message={messageModalText}
        variant={messageModalVariant}
        onConfirm={messageModalAction}
        confirmText={messageModalConfirmText}
      />
    </div>
  );
};

export default WarehouseTaskList;
